import { useState } from "react";
import { motion } from "framer-motion";
import { Check, X, Star, ShieldCheck } from "lucide-react";
import { useAppLaunch } from "@/components/landing/AppLaunch";
import { useLang } from "@/i18n/LanguageContext";

const scrollTo = (id) => {
  const el = document.getElementById(id);
  if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
};

export const Pricing = () => {
  const { t } = useLang();
  const p = t.pricing;
  const { launchApp } = useAppLaunch();
  const [annual, setAnnual] = useState(false);

  const ctaAction = (i) => {
    if (i === 0) scrollTo("telecharger");
    else if (i === 1) scrollTo("byok");
    else launchApp();
  };

  return (
    <section
      id="tarifs"
      data-testid="pricing-section"
      className="relative py-24 sm:py-32 overflow-hidden"
    >
      <div className="pointer-events-none absolute top-40 left-1/2 -translate-x-1/2 h-96 w-96 rounded-full bg-emerald-500/10 blur-3xl" />

      <div className="relative max-w-7xl mx-auto px-6 lg:px-8">
        {/* Header */}
        <div className="text-center max-w-3xl mx-auto">
          <span className="text-xs font-bold uppercase tracking-[0.25em] text-emerald-400">
            {p.overline}
          </span>
          <h2
            data-testid="pricing-slogan"
            className="mt-4 font-outfit text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight text-white"
          >
            {p.slogan}
          </h2>
          <p className="mt-5 text-lg text-slate-400">{p.subtitle}</p>
        </div>

        {/* Billing toggle */}
        <div className="mt-10 flex flex-col items-center gap-3">
          <div
            data-testid="pricing-billing-toggle"
            className="inline-flex items-center rounded-full border border-white/10 bg-white/[0.04] p-1"
          >
            <button
              type="button"
              onClick={() => setAnnual(false)}
              data-testid="billing-monthly"
              className={`rounded-full px-5 py-2 text-sm font-bold transition-colors duration-300 ${
                !annual ? "bg-emerald-400 text-emerald-950" : "text-slate-300 hover:text-white"
              }`}
            >
              {p.billing.monthly}
            </button>
            <button
              type="button"
              onClick={() => setAnnual(true)}
              data-testid="billing-annual"
              className={`flex items-center gap-2 rounded-full px-5 py-2 text-sm font-bold transition-colors duration-300 ${
                annual ? "bg-emerald-400 text-emerald-950" : "text-slate-300 hover:text-white"
              }`}
            >
              {p.billing.annual}
              <span
                className={`rounded-full px-2 py-0.5 text-[0.65rem] font-bold uppercase tracking-wide ${
                  annual ? "bg-emerald-950/20 text-emerald-950" : "bg-emerald-400/15 text-emerald-300"
                }`}
              >
                {p.billing.save}
              </span>
            </button>
          </div>
        </div>

        {/* Cards */}
        <div className="mt-16 grid lg:grid-cols-3 gap-6 lg:gap-5 items-stretch">
          {p.plans.map((plan, i) => {
            const popular = i === 1;
            return (
              <motion.div
                key={i}
                data-testid={`pricing-card-${i}`}
                initial={{ opacity: 0, y: 28 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.1 }}
                className={`relative flex flex-col rounded-3xl border p-8 ${
                  popular
                    ? "border-emerald-400/50 bg-gradient-to-b from-emerald-500/[0.10] to-white/[0.02] lg:-mt-4 lg:mb-[-1rem] shadow-2xl shadow-emerald-500/10 ring-1 ring-emerald-400/20"
                    : "border-white/10 bg-white/[0.03]"
                }`}
              >
                {popular && (
                  <span
                    data-testid="pricing-popular-badge"
                    className="absolute -top-3.5 left-1/2 -translate-x-1/2 inline-flex items-center gap-1.5 rounded-full bg-emerald-400 px-4 py-1.5 text-xs font-bold uppercase tracking-wider text-emerald-950 shadow-lg shadow-emerald-500/30"
                  >
                    <Star className="h-3.5 w-3.5 fill-emerald-950" />
                    {p.popular}
                  </span>
                )}

                <div className="flex items-center gap-3">
                  <span className="text-3xl leading-none" aria-hidden="true">
                    {plan.emoji}
                  </span>
                  <div>
                    <h3 className="font-outfit text-xl font-bold text-white">
                      {plan.name}
                    </h3>
                    {plan.badge && (
                      <span
                        className={`mt-1 inline-block rounded-full px-2.5 py-0.5 text-[0.65rem] font-bold uppercase tracking-wider ${
                          popular
                            ? "bg-emerald-400/20 text-emerald-300"
                            : "bg-violet-400/15 text-violet-300"
                        }`}
                      >
                        {plan.badge}
                      </span>
                    )}
                  </div>
                </div>

                <div className="mt-6 flex items-end gap-1.5">
                  <span
                    data-testid={`pricing-price-${i}`}
                    className="font-outfit text-4xl font-extrabold text-white"
                  >
                    {annual ? plan.priceAnnual : plan.price}
                  </span>
                  <span className="mb-1.5 text-sm text-slate-400">
                    {annual ? p.perYear : p.perMonth}
                  </span>
                </div>
                <p className="mt-1 text-xs text-slate-500">
                  {annual && plan.annualEq ? `${plan.annualEq} · ${plan.priceNote}` : plan.priceNote}
                </p>

                <p className="mt-5 text-sm text-slate-300 leading-relaxed">
                  {plan.tagline}
                </p>

                <button
                  type="button"
                  onClick={() => ctaAction(i)}
                  data-testid={`pricing-cta-${i}`}
                  className={`mt-6 inline-flex items-center justify-center rounded-full px-6 py-3.5 text-sm font-bold uppercase tracking-wider transition-colors duration-300 ${
                    popular
                      ? "bg-emerald-400 text-emerald-950 hover:bg-emerald-300 shadow-lg shadow-emerald-500/25"
                      : "bg-white/10 text-white hover:bg-white/15 border border-white/10"
                  }`}
                >
                  {plan.cta}
                </button>

                <div className="mt-7 flex-1">
                  {plan.includes && (
                    <p className="mb-3 text-xs font-bold uppercase tracking-wider text-emerald-400">
                      {plan.includes}
                    </p>
                  )}
                  <ul className="space-y-3">
                    {plan.features.map((f, fi) => (
                      <li key={fi} className="flex items-start gap-2.5 text-sm text-slate-300">
                        <Check className="mt-0.5 h-4 w-4 shrink-0 text-emerald-400" strokeWidth={2.75} />
                        <span className="leading-snug">{f}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                {plan.note && (
                  <p className="mt-6 rounded-xl border border-white/10 bg-white/[0.02] p-3 text-xs text-slate-400 leading-relaxed">
                    {plan.note}
                  </p>
                )}
              </motion.div>
            );
          })}
        </div>

        {/* Guarantee */}
        <div
          data-testid="pricing-guarantee"
          className="mt-10 flex items-center justify-center gap-2.5 text-center text-sm text-slate-400"
        >
          <ShieldCheck className="h-4 w-4 shrink-0 text-emerald-400" />
          <span>{p.guarantee}</span>
        </div>

        {/* Comparison table */}
        <div className="mt-20">
          <h3 className="text-center font-outfit text-2xl sm:text-3xl font-extrabold text-white">
            {p.compareTitle}
          </h3>

          <div
            data-testid="pricing-compare-table"
            className="mt-8 overflow-x-auto rounded-2xl border border-white/10"
          >
            <table className="w-full min-w-[640px] border-collapse text-sm">
              <thead>
                <tr className="bg-white/[0.04]">
                  {p.compareCols.map((c, ci) => (
                    <th
                      key={ci}
                      className={`px-5 py-4 font-outfit font-bold ${
                        ci === 0
                          ? "text-left text-slate-300"
                          : ci === 2
                          ? "text-center text-emerald-300"
                          : "text-center text-white"
                      }`}
                    >
                      {c}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {p.compareRows.map((row, ri) => (
                  <tr
                    key={ri}
                    className="border-t border-white/[0.06] hover:bg-white/[0.02]"
                  >
                    <td className="px-5 py-3.5 text-slate-300">{row.label}</td>
                    {row.tiers.map((val, ti) => (
                      <td
                        key={ti}
                        className={`px-5 py-3.5 text-center ${
                          ti === 1 ? "bg-emerald-500/[0.04]" : ""
                        }`}
                      >
                        {val ? (
                          <Check className="inline h-5 w-5 text-emerald-400" strokeWidth={2.75} />
                        ) : (
                          <X className="inline h-5 w-5 text-slate-600" strokeWidth={2.5} />
                        )}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </section>
  );
};
